// HomePage (C-HOME) — mirrors review/commerce-app/home.jsx
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/HomePage.tsx. The layout, the copy and the
// four-tile composition are the handoff's; every departure is marked ADAPTED
// and says why. As in AppShell.tsx, design-system components are imported by
// module path rather than from the barrel, and button handlers are `onPress` —
// the React Aria event the DS Button is built on.
//
// ADAPTED (the point of this phase): the page reads live data. The handoff's
// four KPIs, its pending-subscription banner and its activity feed came from
// fixtures with their numbers pre-formatted; they now come from
// GET /portal/api/packs/, /billing/ and /activity/, which send exact decimals
// and ISO timestamps and leave the formatting to ../format.ts.
//
// ADAPTED: the "Current period estimate" tile carries an Estimate badge and
// names AWS Marketplace as the invoice authority. Every amount the backend
// composes arrives marked `estimate: true` — this service is not the invoicing
// authority — and a figure that size shown without that word beside it is a
// figure a customer reads as the amount owed (Owner D4).
//
// BOTH OF THE DROP'S REMAINING FIXTURES ARE GONE. The metered-usage chart
// reads live metering (`firstDimensionSeries(metering.data)`), and an absent
// dimension is an empty series rather than invented rows — see the note at
// its site. The notification bell was removed outright by M-W38-02: it listed
// dummy notifications and no producer existed to fill it.
import type { ReactNode } from "react";
import { Server, Package, Gauge, Receipt, ShoppingCart, Users, ChevronRight, type LucideIcon } from "lucide-react";
import { Badge } from "@miq/design-system/components/ui/badge";
import { Button } from "@miq/design-system/components/ui/button";
import {
  Card, CardAction, CardContent, CardHeader, CardTitle, CardDescription,
} from "@miq/design-system/components/ui/card";
import { UsageStackedBars } from "../components/charts/UsageChart";
import { readMetering } from "../data/reads";
import type { MeteringScreen } from "../data/reads";
import type { UsageChartSeries } from "../components/charts/UsageChart";
import { usePortalRead } from "../data/usePortalRead";
import { monthLabel } from "../data/format";
import { FailedToLoad, LoadingCard, LoadingTile, NothingYet } from "../components/DataStates";
import {
  activityLine, fromChannel, invoicedBy, soleChannel,
  useActivity, useBilling, usePacks,
  type ActivityEvent, type HeldPack, type PacksPayload,
} from "../data/catalogue";
import { isWaitingOnActivation } from "../data/presentation";
import { actsForOneCustomer } from "../auth/api";
import { usePortalSession } from "../auth/context";
import { money, periodSpan, sinceThen } from "../format";
import type { CmRoute } from "../components/AppShell";
import { useDocumentTitle } from "../../lib/documentTitle";

// ADAPTED: the handoff's feed maps a `users` kind too. The derived feed composes
// from commerce records only and has no user-management event to compose from —
// no table records an invitation — so this map has four entries rather than
// five. Keyed on the payload's own union, so a fifth kind arriving on the
// backend is a type error here rather than a blank icon on the page.
const FEED_ICON: Record<ActivityEvent["kind"], LucideIcon> = { subscription: ShoppingCart, metering: Gauge, fleet: Server, billing: Receipt };
const FEED_TONE: Record<ActivityEvent["tone"], string> = { ok: "bg-success-soft text-success", info: "bg-accent text-muted-foreground", warn: "bg-warning-soft text-warning" };

/** Shown for an event kind this build does not know. Never expected to render. */
const FEED_FALLBACK_ICON: LucideIcon = Users;

/**
 * One statistic.
 *
 * ADAPTED: takes an optional `estimate` flag. The handoff had no such notion —
 * every figure was a string somebody typed — and the one tile here that shows
 * money shows a number this service composed rather than one a channel
 * invoiced.
 */
function Kpi({ label, icon: KIcon, value, sub, estimate = false }: {
  label: string;
  icon: LucideIcon;
  value: ReactNode;
  sub: string;
  estimate?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">{label}<KIcon className="size-3.5" /></div>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="text-2xl font-semibold tracking-tight">{value}</span>
          {estimate && <Badge variant="outline">Estimate</Badge>}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

/**
 * The months this chart spans, said plainly — or nothing before it loads.
 *
 * Home's card used to be captioned "Illustrative series", which was true and
 * was the whole problem: it described the data's PROVENANCE in small grey text
 * while the chart beside it looked exactly like a real one. A caption should
 * say what the reader is looking at, and once the numbers are real the honest
 * caption is simply which months they cover.
 */
function meteringSpan(
  metering: ReturnType<typeof usePortalRead<MeteringScreen>>,
): string | null {
  if (metering.status !== "ready") return null;
  const months = metering.data.usage_by_month.months;
  if (months.length === 0) return null;
  const span = `${monthLabel(months[0])} – ${monthLabel(months[months.length - 1])}`;
  return `${span} · monthly totals, stacked by instance`;
}

/**
 * The first dimension's series, because Home has room for one chart.
 *
 * Dimensions are not summable — one measures volume, another well-days — so
 * the Metering screen draws a chart each and lets a person choose. Home shows
 * the first and links to the rest rather than inventing a total in no unit,
 * which is the same reasoning `UsageDimensionChart` is written around.
 *
 * No dimensions is an empty series, not a fixture: the chart renders its own
 * empty state and says nothing has been recorded.
 */
function firstDimensionSeries(screen: MeteringScreen): UsageChartSeries[] {
  const first = screen.usage_by_month.dimensions[0];
  return (first?.series ?? []).map((series) => ({
    name: series.instance,
    quantities: series.quantities,
  }));
}

/**
 * Home, for the customer this session acts for.
 *
 * Two ways out, and they are different kinds of thing, so they are different
 * props. `onNavigate` goes to a nav destination — a screen that exists whoever
 * you are. `onProvision` goes to ONE subscription's setup, which needs to know
 * which subscription, and so takes its public id.
 */
export function HomePage({
  onNavigate,
  onProvision,
}: {
  onNavigate: (r: CmRoute) => void;
  onProvision: (subscriptionId: string) => void;
}) {
  useDocumentTitle('Home');
  const { session } = usePortalSession();
  const packs = usePacks();
  const billing = useBilling();
  const activity = useActivity();
  // The SAME read the Metering screen runs. Home used to draw a fixture here
  // and caption it "illustrative"; a customer with real usage was shown
  // invented numbers under a heading that says charges are billed through the
  // channel. One read, one truth, on both screens.
  const metering = usePortalRead(readMetering);

  // Every read on this page is a customer endpoint, which a staff token is
  // refused at the door — so this is a customer's Home and nobody else's, and
  // PortalApp routes a staff session to All Customers rather than here. The
  // narrowing says so in the type rather than in a comment alone; the empty
  // eyebrow it falls back to is the one the pre-boot case has always had, not
  // a new state.
  const customerName =
    session.status === "signed-in" && actsForOneCustomer(session.identity)
      ? session.identity.customer.name
      : "";

  return (
    <div className="space-y-5 p-6">
      <div>
        {/* ADAPTED: the eyebrow was the fixture's AWS account number. Nothing in
            this service records one — it is the channel's identifier for the
            payer, not ours — so it names which customer this session acts for,
            which is real, and is what somebody holding two of them needs. */}
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Commerce · {customerName}</div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Home</h1>
        <p className="mt-1 text-sm text-muted-foreground">Decision packs, instance fleet, and usage at a glance. Charges are invoiced by the sales channel that sold each subscription.</p>
      </div>

      {packs.status === "ready" && <PendingSetup packs={packs.data.packs} onProvision={onProvision} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {packs.status === "loading" && <><LoadingTile /><LoadingTile /><LoadingTile /></>}
        {packs.status === "failed" && (
          <div className="col-span-full"><FailedToLoad what="Your decision packs" error={packs.error} /></div>
        )}
        {packs.status === "ready" && <FleetAndPackTiles data={packs.data} />}

        {billing.status === "loading" && <LoadingTile />}
        {billing.status === "failed" && <FailedToLoad what="This period's estimate" error={billing.error} />}
        {billing.status === "ready" && (
          <Kpi
            label="Current period estimate"
            icon={Receipt}
            value={money(billing.data.current_period.amount, billing.data.current_period.currency)}
            estimate={billing.data.current_period.estimate}
            sub={`${periodSpan(billing.data.current_period_start)} · ${invoicedBy(soleChannel(billing.data.lines))}`}
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Card>
          {/* ADAPTED: the handoff lays this header out with flex, but the DS
              CardHeader is a grid with a dedicated right-hand slot. CardAction
              puts the link where the prototype shows it. */}
          <CardHeader>
            <CardTitle className="text-base">Metered usage</CardTitle>
            <CardDescription>
              {meteringSpan(metering) ?? "Monthly totals, stacked by instance"}
            </CardDescription>
            <CardAction>
              <Button variant="link" size="sm" className="h-auto p-0" onPress={() => onNavigate("metering")}>
                Open Metering <ChevronRight className="size-3.5" />
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent>
            {metering.status === "loading" && <LoadingCard lines={4} />}
            {metering.status === "failed" && (
              <FailedToLoad what="Metered usage" error={metering.message} />
            )}
            {metering.status === "ready" && (
              <UsageStackedBars
                height={190}
                months={metering.data.usage_by_month.months.map(monthLabel)}
                series={firstDimensionSeries(metering.data)}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {activity.status === "loading" && <LoadingCard lines={4} />}
            {activity.status === "failed" && <FailedToLoad what="Recent activity" error={activity.error} />}
            {activity.status === "ready" && activity.data.events.length === 0 && (
              <NothingYet>Nothing has happened on this account yet.</NothingYet>
            )}
            {activity.status === "ready" && activity.data.events.map((event) => {
              const FIcon = FEED_ICON[event.kind] ?? FEED_FALLBACK_ICON;
              return (
                <div key={`${event.event}-${event.subject}-${event.occurred_at}`} className="flex items-start gap-3">
                  <span className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ${FEED_TONE[event.tone]}`}><FIcon className="size-3.5" /></span>
                  <div>
                    <div className="text-sm leading-snug">{activityLine(event)}</div>
                    {/* The instant travels and the words are computed here, so a
                        tab left open overnight does not still say "2 hours ago". */}
                    <div className="mt-0.5 text-xs text-muted-foreground">{sinceThen(event.occurred_at)}</div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * The banner for a subscription that has arrived and is waiting on setup.
 *
 * ADAPTED: the handoff finds it with `CM_PACKS.find(s => s.status === "pending")`
 * — a pack whose status is pending. Live, the pending thing is the SUBSCRIPTION:
 * its decision pack is chosen at setup, so the entry has no pack at all. That is
 * why the payload's entries are agreements rather than catalogue rows.
 */
function PendingSetup({ packs, onProvision }: { packs: HeldPack[]; onProvision: (subscriptionId: string) => void }) {
  // Not an internal agreement: nothing will ever activate one, so there is no
  // setup to finish — see ../data/presentation.ts.
  const waiting = packs.find(isWaitingOnActivation);
  if (!waiting) {
    return null;
  }

  return (
    <Card className="border-link/30 bg-accent">
      <CardContent className="flex items-center gap-4 py-4">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-link"><ShoppingCart className="size-4.5" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">New subscription received{fromChannel(waiting.channel)}</div>
          <div className="text-xs text-muted-foreground">
            {waiting.agreement_ref} · received {sinceThen(waiting.received_at)}. Finish setup to provision the instance.
          </div>
        </div>
        <Button onPress={() => onProvision(waiting.public_id)}>Finish setup</Button>
      </CardContent>
    </Card>
  );
}

/**
 * The three tiles that come from the packs read.
 *
 * ADAPTED: the handoff hardcodes the decision-pack tile's value (`2`) and its
 * subtitle ("Gas Aggregator · Field Optimization · 1 pending"). Both are derived
 * here, which is most of the difference between a screenshot and a screen.
 */
function FleetAndPackTiles({ data }: { data: PacksPayload }) {
  const active = data.packs.filter((entry) => entry.billable && entry.pack);
  const waiting = data.packs.filter(isWaitingOnActivation);
  const names = active.map((entry) => entry.pack?.name).filter(Boolean);

  return (
    <>
      <Kpi
        label="Instances"
        icon={Server}
        value={data.instances.total}
        sub={`${data.instances.running} running`}
      />
      <Kpi
        label="Decision packs"
        icon={Package}
        value={<>{active.length} <span className="text-sm font-medium text-muted-foreground">active</span></>}
        sub={
          [names.join(" · "), waiting.length ? `${waiting.length} pending setup` : ""]
            .filter(Boolean)
            .join(" · ") || "None yet"
        }
      />
      <Kpi
        label="Dimensions priced"
        icon={Gauge}
        value={data.dimensions_reporting}
        // ADAPTED: the handoff's subtitle named the last metering report. That
        // timestamp is a metering read; this counts what the catalogue charges
        // for, which is the fact this page's own endpoint knows.
        sub={`Across ${active.length} decision pack${active.length === 1 ? "" : "s"}`}
      />
    </>
  );
}
